import { useCallback } from 'react';
import { Map as NewbuildingMapComponent } from '@cian/frontend-newbuilding-map-component';
import { toRegularGeoObjects, toTransportAccessibilityGeoObjects } from '../helpers/mappers';
import { IRegularInfrastructureData, ITAInfrastructureData, TTAInfrastructureType } from '../../../types/map';

interface IUseSetGeoObjectsCallbacksResult {
  /**
   * Устанавливает геообъекты обычной инфраструктуры по типам и году.
   */
  setRegularGeoObjects: (currentTypes: string[], selectedYear: number, isMapServicesVisible: boolean) => void;
  /**
   * Устанавливает геообъекты сервиса транспортной доступности по типу.
   */
  setTransportAccessibilityGeoObjects: (infrastructureType: TTAInfrastructureType | null) => void;
}

/**
 * Возвращает функции установки геообъектов на карту.
 */
export const useSetGeoObjectsCallbacks = (
  mapApi: NewbuildingMapComponent | null,
  regularInfrastructureByYear: Map<number, IRegularInfrastructureData>,
  transportAccessibilityInfrastructureByType: Map<TTAInfrastructureType, ITAInfrastructureData>,
  currentTypes: string[],
  selectedYear: number,
  setFutureTypes: (selectedYear: number) => void,
  resetTARouting: () => void,
): IUseSetGeoObjectsCallbacksResult => {
  const setRegularGeoObjects = useCallback(
    (currentTypes: string[], selectedYear: number, isMapServicesVisible: boolean) => {
      const geoObjects = toRegularGeoObjects(regularInfrastructureByYear, selectedYear, isMapServicesVisible);
      const geoObjectsByType = geoObjects.filter(item => currentTypes.includes(item.name));

      // TODO: Будет удалено при выпиле фича-флага в NB-1153.
      if (!isMapServicesVisible) {
        setFutureTypes(selectedYear);
      }

      mapApi?.removeAllGeoObjects();
      mapApi?.addGeoObjects(geoObjectsByType);
    },
    [mapApi, regularInfrastructureByYear, setFutureTypes],
  );

  const setTransportAccessibilityGeoObjects = useCallback(
    (infrastructureType: TTAInfrastructureType | null) => {
      if (infrastructureType) {
        const geoObjects = toTransportAccessibilityGeoObjects(
          transportAccessibilityInfrastructureByType,
          infrastructureType,
        );

        mapApi?.removeAllGeoObjects();
        mapApi?.addGeoObjects(geoObjects);
      } else {
        setRegularGeoObjects(currentTypes, selectedYear, true);
      }
      resetTARouting();
    },
    [
      mapApi,
      transportAccessibilityInfrastructureByType,
      currentTypes,
      selectedYear,
      setRegularGeoObjects,
      resetTARouting,
    ],
  );

  return {
    setRegularGeoObjects,
    setTransportAccessibilityGeoObjects,
  };
};
